import * as React from "react";
import { ChevronDown, ChevronLeft, ChevronRight } from "lucide-react";
import { DayPicker } from "react-day-picker";
import { useTranslation } from "react-i18next";
import { de, enUS, sr } from "date-fns/locale";
import type { Locale } from "date-fns";

import { cn } from "@/shared/lib/utils";
import { buttonVariants } from "@/shared/components";

const LOCALE_MAP: Record<string, Locale> = {
  de,
  en: enUS,
  enUS,
  sr,
};

export type CalendarProps = React.ComponentProps<typeof DayPicker>;

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  locale: localeProp,
  captionLayout = "dropdown",
  fromYear,
  toYear,
  ...props
}: CalendarProps) {
  const { i18n } = useTranslation();
  const locale =
    localeProp ?? LOCALE_MAP[i18n.language] ?? LOCALE_MAP[i18n.language.split("-")[0]] ?? enUS;
  const startYear = fromYear ?? new Date().getFullYear() - 10;
  const endYear = toYear ?? new Date().getFullYear() + 10;

  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      locale={locale}
      captionLayout={captionLayout}
      fromYear={captionLayout === "dropdown" ? startYear : undefined}
      toYear={captionLayout === "dropdown" ? endYear : undefined}
      className={cn("p-3", className)}
      classNames={{
        vhidden: "sr-only",
        months: "flex flex-col sm:flex-row space-y-4 sm:space-x-4 sm:space-y-0",
        month: "space-y-4",
        caption:
          "flex justify-center py-3 -mx-3 -mt-3 px-3 mb-1 rounded-t-lg bg-muted/30 border-b border-border/50",
        caption_label: "text-sm font-medium text-foreground",
        caption_dropdowns: "flex items-center gap-2 justify-center",
        dropdown_month:
          "relative inline-flex h-8 min-w-[6rem] items-center justify-center rounded-md border border-border/60 bg-background/80 px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50 hover:border-border",
        dropdown_year:
          "relative inline-flex h-8 min-w-[4.5rem] items-center justify-center rounded-md border border-border/60 bg-background/80 px-2.5 text-sm font-medium text-foreground transition-colors hover:bg-muted/50 hover:border-border",
        dropdown:
          "absolute inset-0 z-10 h-full w-full cursor-pointer opacity-0",
        dropdown_icon: "ml-1 h-3.5 w-3.5 shrink-0 text-muted-foreground",
        nav: "space-x-1 flex items-center",
        nav_button: cn(
          buttonVariants({ variant: "outline" }),
          "h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100",
        ),
        nav_button_previous: "absolute left-1",
        nav_button_next: "absolute right-1",
        table: "w-full border-collapse space-y-1",
        head_row: "flex",
        head_cell: "text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
        row: "flex w-full mt-2",
        cell: "h-9 w-9 text-center text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20",
        day: cn(buttonVariants({ variant: "ghost" }), "h-9 w-9 p-0 font-normal aria-selected:opacity-100"),
        day_range_end: "day-range-end",
        day_selected:
          "bg-primary text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
        day_today: "bg-accent text-accent-foreground",
        day_outside:
          "day-outside text-muted-foreground opacity-50 aria-selected:bg-accent/50 aria-selected:text-muted-foreground aria-selected:opacity-30",
        day_disabled: "text-muted-foreground opacity-50",
        day_range_middle: "aria-selected:bg-accent aria-selected:text-accent-foreground",
        day_hidden: "invisible",
        ...classNames,
      }}
      components={{
        IconLeft: () => <ChevronLeft className="h-4 w-4" />,
        IconRight: () => <ChevronRight className="h-4 w-4" />,
        IconDropdown: ({ className, style }) => (
          <ChevronDown className={cn("h-4 w-4", className)} style={style} />
        ),
      }}
      {...props}
    />
  );
}
Calendar.displayName = "Calendar";

export { Calendar };
